/**
 * Where `@rista/printing`'s status claims are written — v1's exactly (strict parity, decided
 * 2026-09-15): online through the SAME sagas v1 dispatches, so their side effects
 * (`updateSyncedSale`, `updateAdhocTables`) still run; offline through `updateAllTicketsPrintStatus`.
 *
 * One implementation for both platforms. This was a `status/` split while mobile had no
 * `printLabelSlice`, no watcher for one and no way to poll for labels at all; mobile has all three
 * as of 2026-09-16 (`redux-store/saga/printer/index.native.ts`), so the divergence — and the split —
 * is gone. Both variants resolve `printLabelSlice` from their own TableViewSlice.
 */
import type { LabelStatus, StatusResult, StatusSink } from "@rista/printing";

import store from "../redux-store";
import { printKOTSlice, printLabelSlice } from "../redux-store/slices/TableViewSlice";
import { isOffline } from "../facade/ServiceFacade";
import { updateAllTicketsPrintStatus } from "../helpers/OfflineHelper";

/** v1 `polling.js` `updateLabelStatus`. */
export type LabelWriter = (saleId: string, labelInfos: LabelStatus[]) => Promise<StatusResult>;

const tickets: StatusSink["tickets"] = (saleId, ticketInfos) =>
  new Promise<StatusResult>((resolve) => {
    if (isOffline()) {
      // v1 printKOT prints when neither `res.code` nor `err` is set
      updateAllTicketsPrintStatus({
        id: saleId,
        ticketInfos,
        cb: (res: any, err: any) => resolve(err ? "unreachable" : res?.code ? "rejected" : "accepted"),
      });
      return;
    }
    store.dispatch(
      printKOTSlice({
        id: saleId,
        ticketInfos,
        // v1 printKOT prints when the response carries the branch or its tickets
        callback: (response: any) => resolve(response?.branchId || response?.tickets ? "accepted" : "rejected"),
        errorCallback: () => resolve("unreachable"),
      }),
    );
  });

/**
 * v1 `polling.js` `updateLabelStatus` — offline it answers success without writing anything, so a
 * poller that is offline still prints its labels.
 */
const labels: LabelWriter = (saleId, labelInfos) =>
  new Promise((resolve) => {
    if (isOffline()) return resolve("accepted");
    store.dispatch(
      printLabelSlice({
        id: saleId,
        labelInfos,
        // v1 does NOT re-check `response.code` here as it does for tickets — the saga already did
        callback: () => resolve("accepted"),
        // The saga funnels BOTH refusals into this one callback: a 200 whose body carries a `code`
        // (it passes that RESPONSE) and a request that threw (it passes what `apiService` rejected
        // with — `error.response`, i.e. an axios response object with no top-level `code`, or
        // `undefined` when there was no response at all). Only the first is the server SAYING no,
        // which is what `printLabels` retries once, 4 s later, exactly as the ticket path does.
        // Without this the label path could only ever answer "unreachable", and that retry —
        // ledger row 32 — was unreachable code.
        errorCallback: (answer: any) => resolve(answer?.code ? "rejected" : "unreachable"),
      }),
    );
  });

export function statusSink(labelWriter: LabelWriter = labels): StatusSink {
  return { tickets, labels: labelWriter };
}

export const printStatusSink = statusSink();
